/**
 * SearchableSelectController — composant transverse `searchable-select`
 * (EPIC-099.50). Combobox ARIA recherchable remplaçant un `<select>` natif
 * par une saisie filtrante, pour les sélecteurs d'entité à fort cardinal
 * (client, article de catalogue, …).
 *
 * Pattern WCAG 2.1 AA « combobox + listbox » en vanilla + Stimulus :
 *   - input texte (`role="combobox"`, `aria-expanded`, `aria-autocomplete="list"`,
 *     `aria-controls`, `aria-activedescendant`) ;
 *   - liste de suggestions (`role="listbox"`, options `role="option"`,
 *     `aria-selected`) réutilisant le style `.nm-autocomplete` (EPIC-042.5) ;
 *   - navigation clavier complète (↑/↓, Entrée, Échap, Home/End, Tab) ;
 *   - focus roulant via `aria-activedescendant` (pas de vol de focus).
 *
 * La **valeur sélectionnée (id/UUID)** est écrite dans un `<input type="hidden">`
 * distinct du libellé affiché — le formulaire/parent lit l'input caché, jamais
 * le texte visible (D3).
 *
 * Deux sources de données (D4) :
 *   - `mode="remote"` (défaut) : fetch debouncé (250 ms) via un *adapter*
 *     identifié par `endpoint-value` (`clients`, `articles`). Réutilise le
 *     param `q` ILIKE déjà présent côté backend — aucun changement serveur.
 *   - `mode="static"` : filtrage client-side d'une liste préchargée passée par
 *     le parent via la méthode publique `setItems(items)`.
 *
 * Événements émis :
 *   - `searchable-select:change` → `{ id, label, item }` à chaque sélection
 *     (ou `{ id: "", label: "", item: null }` quand la sélection est effacée).
 *
 * Pré-remplissage programmatique (mode édition) : `setValue({ id, label })`.
 *
 * Attributs `data-*` (sur l'élément racine `[data-controller="searchable-select"]`) :
 *   - `data-searchable-select-mode-value`     : "remote" | "static" (défaut "remote")
 *   - `data-searchable-select-endpoint-value` : clé d'adapter ("clients" | "articles")
 *   - `data-searchable-select-min-chars-value`: seuil de déclenchement (défaut 1)
 *   - `data-searchable-select-per-page-value` : nb max de suggestions (défaut 8)
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { debounce } from "/js/utils/debounce.js";

/**
 * Formatte un montant HT pour l'affichage d'un article. Local au composant
 * pour ne pas dépendre du contrôleur parent.
 *
 * @param {number|string} value
 * @returns {string}
 */
function formatMoney(value) {
  const num = Number(value ?? 0);
  return (Number.isFinite(num) ? num : 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Adapters de source `remote`. Chaque adapter mappe la réponse serveur vers
 * la forme normalisée `{ id, label, secondary, item }` consommée par le
 * composant. Ajouter une entrée ici pour brancher un nouveau type d'entité.
 */
const ADAPTERS = {
  clients: async (q, perPage) => {
    // Requête vide (ouverture du menu sans saisie) : liste « initiale » des
    // clients les plus récents. Sinon : filtre `q` (le tri serveur par défaut
    // est déjà `created_at desc`).
    const params = { "filter[status]": "active", perPage };
    if (q) params.q = q;
    else params.sort = "-createdAt";
    const resp = await api.listClients(params);
    const list = Array.isArray(resp?.data) ? resp.data : [];
    return list.map((c) => ({
      id: c.id,
      label: c.legalName,
      secondary: c.siret ? `SIRET ${c.siret}` : "",
      item: c,
    }));
  },
  articles: async (q, perPage) => {
    // Requête vide : articles les plus récents. À la frappe : filtre `q` trié
    // par libellé (plus pertinent pour une recherche texte).
    const params = { "filter[status]": "active", perPage };
    if (q) {
      params.q = q;
      params.sort = "label";
    } else {
      params.sort = "-createdAt";
    }
    const resp = await api.listArticles(params);
    const list = Array.isArray(resp?.data) ? resp.data : [];
    return list.map((a) => ({
      id: a.id,
      label: a.label,
      secondary: `${formatMoney(a.priceExclVat)} € HT`,
      item: a,
    }));
  },
};

export default class SearchableSelectController extends BaseController {
  static targets = ["input", "hidden", "listbox"];

  static values = {
    mode: { type: String, default: "remote" },
    endpoint: { type: String, default: "" },
    minChars: { type: Number, default: 1 },
    perPage: { type: Number, default: 8 },
  };

  connect() {
    this._options = []; // résultats normalisés affichés
    this._items = []; // cache mode static (setItems)
    this._activeIndex = -1;
    this._listId =
      this.hasListboxTarget && this.listboxTarget.id
        ? this.listboxTarget.id
        : `ss-list-${Math.round(performance.now())}`;
    if (this.hasListboxTarget) this.listboxTarget.id = this._listId;
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("role", "combobox");
      this.inputTarget.setAttribute("aria-expanded", "false");
      this.inputTarget.setAttribute("aria-autocomplete", "list");
      this.inputTarget.setAttribute("aria-controls", this._listId);
      this.inputTarget.setAttribute("autocomplete", "off");
    }
    this._searchDebounced = debounce(() => this._runSearch(), 250);
    this._onDocPointer = (event) => this._handleOutsideClick(event);
    document.addEventListener("mousedown", this._onDocPointer);
  }

  disconnect() {
    if (this._searchDebounced?.cancel) this._searchDebounced.cancel();
    document.removeEventListener("mousedown", this._onDocPointer);
  }

  // ---------------------------------------------------------------------------
  // API publique (consommée par le contrôleur parent)
  // ---------------------------------------------------------------------------

  /**
   * Alimente la source `static`. Items attendus au format brut de l'adapter
   * `endpoint` correspondant (la normalisation est refaite ici).
   *
   * @param {Array<object>} items
   */
  setItems(items) {
    this._items = Array.isArray(items) ? items : [];
  }

  /**
   * Pré-remplit la sélection (mode édition) sans déclencher de recherche ni
   * d'événement `change`.
   *
   * @param {{id: string, label: string}} value
   */
  setValue({ id, label } = {}) {
    if (this.hasHiddenTarget) this.hiddenTarget.value = id ?? "";
    if (this.hasInputTarget) this.inputTarget.value = label ?? "";
  }

  /** Vide la sélection (libellé + id) sans émettre d'événement. */
  clear() {
    this.setValue({ id: "", label: "" });
    this._close();
  }

  // ---------------------------------------------------------------------------
  // Saisie & recherche
  // ---------------------------------------------------------------------------

  onInput() {
    // Toute frappe invalide une sélection antérieure : on repasse « non choisi »
    // jusqu'à validation explicite d'une option.
    if (this.hasHiddenTarget && this.hiddenTarget.value) {
      this.hiddenTarget.value = "";
      this._emitChange(null);
    }
    if (this.modeValue === "static") {
      this._runSearch();
    } else {
      this._searchDebounced();
    }
  }

  onFocus() {
    // Comportement « menu déroulant » : à l'ouverture du champ, on affiche la
    // liste immédiatement. Si une sélection est déjà committée, on rouvre sur
    // la liste initiale (comme un select) et on présélectionne le texte pour
    // permettre de retaper par-dessus. Recherche directe (non debouncée).
    const hasSelection = this.hasHiddenTarget && this.hiddenTarget.value !== "";
    if (hasSelection && this.hasInputTarget) this.inputTarget.select();
    this._runSearch(hasSelection);
  }

  async _runSearch(forceInitial = false) {
    const raw = (this.hasInputTarget ? this.inputTarget.value : "").trim();
    const query = forceInitial ? "" : raw;
    if (query.length < this.minCharsValue) {
      this._renderOptions([]);
      this._close();
      return;
    }
    let options = [];
    try {
      if (this.modeValue === "static") {
        options = this._filterStatic(query);
      } else {
        const adapter = ADAPTERS[this.endpointValue];
        options = adapter ? await adapter(query, this.perPageValue) : [];
      }
    } catch {
      options = [];
    }
    this._renderOptions(options);
    this._open(); // ouvre toujours : liste de résultats ou message « Aucun résultat »
  }

  _filterStatic(query) {
    const adapterKey = this.endpointValue;
    // En static, on normalise localement chaque item d'une liste déjà chargée
    // (setItems), puis on filtre client-side sur le libellé ou le secondaire.
    const mapped = this._items.map((raw) =>
      this._normalizeStaticItem(raw, adapterKey),
    );
    const needle = query.toLowerCase();
    return mapped
      .filter(
        (o) =>
          o.label.toLowerCase().includes(needle) ||
          (o.secondary && o.secondary.toLowerCase().includes(needle)),
      )
      .slice(0, this.perPageValue);
  }

  _normalizeStaticItem(raw, adapterKey) {
    if (adapterKey === "articles") {
      return {
        id: raw.id,
        label: raw.label,
        secondary: `${formatMoney(raw.priceExclVat)} € HT`,
        item: raw,
      };
    }
    if (adapterKey === "clients") {
      return {
        id: raw.id,
        label: raw.legalName,
        secondary: raw.siret ? `SIRET ${raw.siret}` : "",
        item: raw,
      };
    }
    return {
      id: raw.id ?? "",
      label: raw.label ?? raw.legalName ?? String(raw.id ?? ""),
      secondary: "",
      item: raw,
    };
  }

  // ---------------------------------------------------------------------------
  // Rendu de la liste
  // ---------------------------------------------------------------------------

  _renderOptions(options) {
    this._options = options;
    this._activeIndex = -1;
    if (!this.hasListboxTarget) return;
    const list = this.listboxTarget;
    while (list.firstChild) list.removeChild(list.firstChild);

    if (options.length === 0) {
      const empty = document.createElement("li");
      empty.className = "nm-autocomplete__item nm-autocomplete__item--empty";
      empty.setAttribute("aria-disabled", "true");
      empty.textContent = "Aucun résultat";
      list.appendChild(empty);
      this._setActiveDescendant(null);
      return;
    }

    options.forEach((opt, index) => {
      const li = document.createElement("li");
      li.className = "nm-autocomplete__item";
      li.id = `${this._listId}-opt-${index}`;
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");
      li.dataset.index = String(index);

      const label = document.createElement("span");
      label.className = "nm-autocomplete__label";
      label.textContent = opt.label ?? "";
      li.appendChild(label);

      if (opt.secondary) {
        const secondary = document.createElement("span");
        secondary.className = "nm-autocomplete__secondary";
        secondary.textContent = opt.secondary;
        li.appendChild(secondary);
      }
      list.appendChild(li);
    });
    this._setActiveDescendant(null);
  }

  // ---------------------------------------------------------------------------
  // Clavier & navigation
  // ---------------------------------------------------------------------------

  onKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this._isOpen()) {
          this._runSearch();
          return;
        }
        this._moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (this._isOpen()) this._moveActive(-1);
        break;
      case "Home":
        if (this._isOpen() && this._options.length) {
          event.preventDefault();
          this._setActive(0);
        }
        break;
      case "End":
        if (this._isOpen() && this._options.length) {
          event.preventDefault();
          this._setActive(this._options.length - 1);
        }
        break;
      case "Enter":
        if (this._isOpen() && this._activeIndex >= 0) {
          event.preventDefault();
          this._commit(this._activeIndex);
        }
        break;
      case "Escape":
        if (this._isOpen()) {
          event.preventDefault();
          this._close();
        }
        break;
      case "Tab":
        this._close();
        break;
      default:
        break;
    }
  }

  _moveActive(delta) {
    if (this._options.length === 0) return;
    let next = this._activeIndex + delta;
    if (next < 0) next = this._options.length - 1;
    if (next >= this._options.length) next = 0;
    this._setActive(next);
  }

  _setActive(index) {
    if (!this.hasListboxTarget) return;
    const items = this.listboxTarget.querySelectorAll('[role="option"]');
    items.forEach((el, i) => {
      el.setAttribute("aria-selected", i === index ? "true" : "false");
    });
    this._activeIndex = index;
    const active = items[index];
    if (active) {
      this._setActiveDescendant(active.id);
      active.scrollIntoView?.({ block: "nearest" });
    }
  }

  _setActiveDescendant(id) {
    if (!this.hasInputTarget) return;
    if (id) this.inputTarget.setAttribute("aria-activedescendant", id);
    else this.inputTarget.removeAttribute("aria-activedescendant");
  }

  // ---------------------------------------------------------------------------
  // Sélection (souris + clavier)
  // ---------------------------------------------------------------------------

  // `mousedown` (et non `click`) pour committer avant le `blur` de l'input.
  onOptionMousedown(event) {
    const li = event.target.closest('[role="option"]');
    if (!li || li.dataset.index === undefined) return;
    event.preventDefault();
    this._commit(Number(li.dataset.index));
  }

  _commit(index) {
    const opt = this._options[index];
    if (!opt) return;
    if (this.hasInputTarget) this.inputTarget.value = opt.label ?? "";
    if (this.hasHiddenTarget) this.hiddenTarget.value = opt.id ?? "";
    this._close();
    this._emitChange(opt);
  }

  _emitChange(opt) {
    this.dispatch("change", {
      detail: {
        id: opt?.id ?? "",
        label: opt?.label ?? "",
        item: opt?.item ?? null,
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Ouverture / fermeture
  // ---------------------------------------------------------------------------

  _open() {
    if (!this.hasListboxTarget) return;
    this.listboxTarget.hidden = false;
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("aria-expanded", "true");
    }
  }

  _close() {
    if (this.hasListboxTarget) this.listboxTarget.hidden = true;
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("aria-expanded", "false");
    }
    this._activeIndex = -1;
    this._setActiveDescendant(null);
  }

  _isOpen() {
    return this.hasListboxTarget && !this.listboxTarget.hidden;
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this._close();
  }
}
